/**
 * Os textos configuráveis do DEPARTAMENTO da conversa.
 *
 * `text_configs` tem uma linha por `service_group_id`: cada departamento
 * escreve a própria mensagem de encerramento e de boas-vindas.
 *
 * ATENÇÃO, e esta frase já esteve ERRADA aqui: a lista do redux NÃO é a do
 * tenant inteiro. Quem a popula é `getTextConfig(user.service_group_id)`
 * (`user.actions.js`, no login) -- ou seja, ela tem a config de UM
 * departamento: o do OPERADOR. A função que buscaria a lista inteira
 * (`getTextConfigs()`, sem argumento) existe e NÃO TEM CHAMADOR, e a rota que
 * ela usaria (`GET /text_configs`) está atrás do middleware `admin`, então o
 * operador comum nem poderia consumi-la.
 *
 * Uma análise anterior leu o controller sem seguir quem chamava, concluiu
 * "a tela tem a lista inteira" e trocou `textConfigs[0]` por uma busca pelo
 * departamento da SALA. Numa lista que só tem o departamento do operador,
 * isso fez toda conversa de outro departamento -- e toda sala sem
 * departamento -- cair no texto genérico. O conserto piorou o caso comum.
 *
 * Por isso a config do departamento certo passou a vir NA PRÓPRIA SALA
 * (`sala.service_group.text_config`, carregada pela API), e a lista do redux
 * virou o degrau de baixo da cadeia -- o que preserva o comportamento antigo
 * em vez de regredir.
 *
 * ## Por que a regra mora aqui e não no componente
 *
 * Porque ela é decidível sem React: entram uma lista e uma sala, sai um texto.
 * Dentro do `ChatOptions` ela só era exercitável renderizando o cabeçalho do
 * chat inteiro e clicando em "Finalizar" -- e um teste assim não distingue
 * "achou a config certa" de "achou alguma config".
 */

/** O texto padrão do encerramento, quando o departamento não configurou nada. */
import { ehSalaDeWhatsapp } from './janelaDoWhatsapp';

const ENCERRAMENTO_PADRAO = (nomeDoOperador) => `Conversa encerrada pelo Operador: ${nomeDoOperador}`;

/**
 * O texto padrão da boas-vindas da transferência.
 *
 * O recuo de quatro espaços na segunda linha veio da indentação do código que
 * montava este texto, e está preservado de propósito: é o que o cliente lê
 * hoje. Arrumar o recuo é mudar a mensagem que sai para o cliente, e isso é
 * decisão de produto -- não faxina de refactor.
 */
const BOAS_VINDAS_PADRAO = (nomeDoOperador) => `Olá, eu sou o ${nomeDoOperador}!
    E a partir de agora irei dar continuidade com este chat. Em que posso ajudar?`;

/**
 * Texto que existe de verdade -- caixa em branco na tela não conta.
 *
 * O `trim()` sozinho não basta, e isso é sobre o Quill: a tela de Configurações
 * usa ReactQuill, e o editor esvaziado NÃO salva string vazia -- ele salva
 * `<p><br></p>`, que atravessa o `trim()` e também o purifier da API. Sem esta
 * checagem, o departamento que limpou a caixa passa a mandar `<p><br></p>` para
 * o cliente em vez de cair no texto padrão.
 *
 * A `<img>` conta como conteúdo, e não é exceção gratuita: o mesmo formulário
 * tem redimensionador de imagem, e uma mensagem que é só uma imagem é
 * configuração que alguém fez de propósito -- o chat do site renderiza. Tratar
 * como vazio apagaria o trabalho da pessoa.
 *
 * A regex exige `[a-zA-Z]` depois do `<` de propósito: `preço < 100` não é tag,
 * e um `strip_tags` ingênuo comeria o resto da frase.
 */
const temConteudo = (texto) => {
    if (typeof texto !== 'string') return false;
    if (texto.trim() === '') return false;

    if (/<img\b/i.test(texto)) return true;

    const semTags = texto
        .replace(/<\/?[a-zA-Z][^>]*>/g, '')
        // O espaço inquebrável do Quill, escapado e cru: nenhum dos dois é
        // espaço para o `trim()`.
        .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
        .replace(/ /g, ' ');

    return semTags.trim() !== '';
};

/**
 * Um id de departamento comparável, ou `null` quando não há departamento.
 *
 * Os dois lados da comparação vêm de payloads diferentes (a sala e a config), e
 * um deles virar string numa serialização não pode trocar a mensagem que o
 * cliente recebe. Já `null`/`undefined` NÃO é um id: sem isto, uma sala sem
 * departamento casaria com uma linha órfã de `text_configs` (`null === null`) e
 * aquela linha passaria a valer para toda sala sem departamento.
 */
const idComparavel = (valor) => {
    if (valor === null || valor === undefined || valor === '') return null;

    return String(valor);
};

/**
 * A configuração de textos de UM departamento, pelo id, ou `null`.
 *
 * É o primitivo. Existe separado de `configDoDepartamento()` porque quem tem o
 * id na mão -- a tela de Configurações tem, pelo `currentUser.service_group_id`
 * -- não precisa montar uma sala de mentira só para consultar. A tela lia
 * `textConfigs[0]` e por isso mostrava o texto de OUTRO departamento na caixa,
 * gravando-o em seguida no departamento do próprio admin.
 */
export const configDoServiceGroup = (textConfigs, serviceGroupId) => {
    if (!Array.isArray(textConfigs)) return null;

    const procurado = idComparavel(serviceGroupId);

    if (procurado === null) return null;

    const achada = textConfigs.find(
        (config) => idComparavel(config?.service_group_id) === procurado
    );

    return achada || null;
};

/**
 * A configuração de textos do departamento da sala, ou `null`.
 *
 * `null` quando a sala não tem departamento, quando o departamento dela não tem
 * config, ou quando a lista ainda não chegou (ela chega vazia no primeiro
 * render, antes de `getTextConfig()` responder -- e finalizar uma conversa
 * nesse instante não pode explodir).
 *
 * Devolver a config de OUTRO departamento em qualquer um desses casos seria
 * pior que devolver nada: o texto padrão é genérico e serve para qualquer
 * conversa; o texto do Suporte, numa conversa do ADM, está simplesmente errado.
 */
export const configDoDepartamento = (textConfigs, sala) =>
    configDoServiceGroup(textConfigs, sala?.service_group_id);

/**
 * A configuração que o SERVIDOR mandou junto da sala, ou `null`. É o degrau 1.
 *
 * `RoomService::show()` faz `with('serviceGroup.textConfig')`, então a sala
 * chega com o departamento DELA e os textos configurados por esse departamento.
 * Esta é a única fonte que sabe o departamento da CONVERSA: a lista do redux
 * sabe o departamento do OPERADOR, que é outra coisa toda vez que o operador
 * não é do grupo que o cliente escolheu.
 *
 * `null` também quando a sala vem sem `service_group` -- API antiga, ou sala com
 * `service_group_id` nulo (salas antigas, anteriores ao departamento na
 * entrada). Nesses casos quem decide é o degrau 2.
 */
export const configDaSala = (sala) => sala?.service_group?.text_config || null;

/**
 * A primeira configuração utilizável da lista -- que é a do OPERADOR.
 *
 * Não é "qualquer uma": `state.serviceGroups.textConfigs` é populada por
 * `getTextConfig(user.service_group_id)` (`user.actions.js`), que pede ao
 * servidor só a linha do departamento do operador logado. A lista inteira do
 * tenant viria de `getTextConfigs()`, que não tem chamador nenhum em `src/`.
 */
const configDoOperador = (textConfigs) => {
    if (!Array.isArray(textConfigs)) return null;

    return textConfigs.find((config) => config && typeof config === 'object') || null;
};

/**
 * A configuração que vale para esta conversa. A ORDEM é o ponto.
 *
 *   1. `sala.service_group.text_config` -- a precisão por departamento, que é o
 *      objetivo: o servidor sabe o departamento da conversa, a tela não.
 *   2. a config que a lista tem, que é a do operador. Dentro do degrau 2 o
 *      departamento da sala ainda tem preferência (`configDoDepartamento`), para
 *      o dia em que `getTextConfigs()` ganhar chamador e a lista passar a ter o
 *      tenant inteiro -- sem isso o degrau 2 voltaria a ser o `[0]` de antes.
 *      Este degrau é o comportamento ANTIGO da tela, e é ele que impede a
 *      regressão: enquanto a sala vier sem config (API antiga no ar, ou sala sem
 *      departamento), a conversa continua recebendo o texto configurado em vez
 *      do genérico.
 *   3. nada -- e quem chamou usa o texto padrão.
 *
 * Um degrau resolve UMA config, e é essa config que decide. Se ela existe e a
 * caixa está em branco, o resultado é o texto padrão, não o texto de OUTRO
 * departamento: caixa em branco é decisão de quem configurou, e o padrão é
 * genérico de propósito -- a despedida do Suporte, numa conversa do Financeiro,
 * está simplesmente errada.
 */
export const configParaAConversa = (textConfigs, sala) =>
    configDaSala(sala)
    || configDoDepartamento(textConfigs, sala)
    || configDoOperador(textConfigs);

/**
 * O texto que sai quando o operador finaliza o atendimento.
 *
 * O sufixo "Atendimento encerrado por: {nome}" acompanha SEMPRE a mensagem
 * configurada, e vai para o WhatsApp também. É requisito do dono do produto,
 * não detalhe de formatação: serve de registro interno e é a resposta para "com
 * quem eu falei?" -- a mensagem do departamento, por si só, não menciona
 * ninguém.
 *
 * ## No WhatsApp este texto NÃO vai mais para o cliente (13/10/2026)
 *
 * Antes ia, e era requisito. Deixou de ser quando a instância do WhatsApp ganhou
 * a SUA mensagem de encerramento: o cliente passava a receber as duas, e a do
 * departamento chegava com um texto escrito para o chat do site. A regra
 * combinada é a separação por canal -- texto da instância só no WhatsApp, texto
 * de Configurações só no chat do site.
 *
 * Sem config (ou com a caixa em branco) o padrão já nomeia o operador, e aí o
 * sufixo seria repetição.
 */
export const mensagemDeEncerramento = (textConfigs, sala, nomeDoOperador) => {
    const config = configParaAConversa(textConfigs, sala);

    if (!config || !temConteudo(config.closing_message)) {
        return ENCERRAMENTO_PADRAO(nomeDoOperador);
    }

    // Em conversa de WhatsApp o texto do DEPARTAMENTO não sai: quem fala com o
    // cliente ali é a instância. Fica só o registro de quem encerrou, e ele vai
    // como mensagem INTERNA -- ver `tipoDoEncerramento`.
    if (ehSalaDeWhatsapp(sala)) {
        return `Atendimento encerrado por: ${nomeDoOperador}`;
    }

    return `${config.closing_message}\n\nAtendimento encerrado por: ${nomeDoOperador}`;
};

/** Mensagem de sistema: o cliente a recebe. */
export const TIPO_SISTEMA = 4;

/** Recado interno: fica no Histórico e NÃO vai para o cliente. */
export const TIPO_INTERNO = 5;

/**
 * O TIPO da mensagem de encerramento: interna no WhatsApp, de sistema no site.
 *
 * No WhatsApp quem se despede é a instância (a mensagem configurada no número),
 * então este registro não pode chegar no celular de ninguém -- mas tem de
 * continuar visível no Histórico, que é onde o operador lê quem encerrou.
 *
 * Antes era sempre 4, e o cliente recebia os DOIS textos: o do departamento
 * (escrito para o chat do site) e o da instância.
 */
export const tipoDoEncerramento = (sala) => (ehSalaDeWhatsapp(sala) ? TIPO_INTERNO : TIPO_SISTEMA);

/**
 * O texto que o novo operador manda ao assumir uma conversa transferida.
 *
 * Aqui o nome NÃO é concatenado: o texto configurado do departamento é usado
 * como está, porque a boas-vindas já se apresenta e quem escreve o texto decide
 * como. Só o padrão nomeia o operador -- e ali é obrigatório, porque o cliente
 * está vendo outra pessoa assumir a conversa.
 */
export const mensagemDeBoasVindas = (textConfigs, sala, nomeDoOperador) => {
    const config = configParaAConversa(textConfigs, sala);

    if (!config || !temConteudo(config.welcome_message)) {
        return BOAS_VINDAS_PADRAO(nomeDoOperador);
    }

    return config.welcome_message;
};
